/**
 * In-game bug reporter — captures screenshot via Screen Capture API (primary)
 * or html2canvas (fallback), attaches console logs, and creates a GitHub issue.
 */

import { fetchAPI } from '../api';
import { fixture } from '../data/fixture';
import { state } from '../main';
import { formatVersion } from '../utils/version';
import { t } from '../i18n';

declare const __APP_VERSION__: string;

interface BugReportResult {
  issueNumber: number;
  issueUrl: string;
}

// ── Console Log Collector ──────────────────────────────────────────────────

const MAX_LOG_ENTRIES = 150;

interface LogEntry {
  ts: number;
  level: string;
  args: string;
}

const logBuffer: LogEntry[] = [];

/** Install console interceptors — call once at app startup. */
export function initConsoleCapture(): void {
  // Guard against double-init
  if ((window as any).__bugLogCaptureInstalled) return;
  (window as any).__bugLogCaptureInstalled = true;

  for (const level of ['log', 'warn', 'error', 'info', 'debug'] as const) {
    const original = console[level].bind(console);
    console[level] = (...args: any[]) => {
      original(...args);
      try {
        const serialized = args.map(a => {
          if (a instanceof Error) return `${a.name}: ${a.message}`;
          if (typeof a === 'object') {
            try { return JSON.stringify(a, null, 0)?.slice(0, 300); }
            catch { return String(a); }
          }
          return String(a);
        }).join(' ');
        logBuffer.push({ ts: Date.now(), level, args: serialized.slice(0, 500) });
        if (logBuffer.length > MAX_LOG_ENTRIES) logBuffer.shift();
      } catch { /* don't let logging break the app */ }
    };
  }

  // Capture unhandled errors
  window.addEventListener('error', (e) => {
    logBuffer.push({
      ts: Date.now(),
      level: 'error',
      args: `[unhandled] ${e.message} at ${e.filename}:${e.lineno}:${e.colno}`,
    });
    if (logBuffer.length > MAX_LOG_ENTRIES) logBuffer.shift();
  });

  window.addEventListener('unhandledrejection', (e) => {
    logBuffer.push({
      ts: Date.now(),
      level: 'error',
      args: `[unhandled-rejection] ${e.reason instanceof Error ? e.reason.message : String(e.reason)}`,
    });
    if (logBuffer.length > MAX_LOG_ENTRIES) logBuffer.shift();
  });
}

function getRecentLogs(maxEntries = 50): string {
  const recent = logBuffer.slice(-maxEntries);
  if (recent.length === 0) return t('bugreport.no_logs');
  return recent.map(e => {
    const time = new Date(e.ts).toLocaleTimeString('de-DE', { hour12: false });
    return `[${time}] ${e.level.toUpperCase().padEnd(5)} ${e.args}`;
  }).join('\n');
}

// ── Screenshot Capture ─────────────────────────────────────────────────────

/**
 * Capture screenshot via Screen Capture API.
 * Returns a data URL or empty string on failure/denial.
 */
async function captureViaScreenCapture(): Promise<string> {
  try {
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: { displaySurface: 'browser' } as any,
      audio: false,
      preferCurrentTab: true,
    } as any);

    // Wait a frame for the stream to be ready
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    await video.play();

    // Give the video a moment to render the first frame
    await new Promise(r => setTimeout(r, 200));

    const canvas = document.createElement('canvas');
    // Downscale large screenshots to max 1920px wide
    const maxW = 1920;
    const scale = video.videoWidth > maxW ? maxW / video.videoWidth : 1;
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Stop all tracks immediately
    stream.getTracks().forEach(track => track.stop());
    video.srcObject = null;

    return canvas.toDataURL('image/jpeg', 0.75);
  } catch {
    // User denied or API unavailable
    return '';
  }
}

/**
 * Capture screenshot via html2canvas (DOM rasterizer).
 * Returns a data URL or empty string on failure.
 */
async function captureViaHtml2Canvas(): Promise<string> {
  try {
    const html2canvas = (await import('html2canvas')).default;
    const content = document.getElementById('content');
    if (!content) return '';

    const canvas = await html2canvas(content, {
      backgroundColor: '#0a0e18',
      scale: Math.min(1, 1920 / content.scrollWidth),
      logging: false,
      useCORS: true,
      allowTaint: true,
      width: Math.min(content.scrollWidth, 1920),
      height: Math.min(content.scrollHeight, 1080),
    });
    return canvas.toDataURL('image/jpeg', 0.75);
  } catch {
    return '';
  }
}

/**
 * Capture screenshot: try Screen Capture API first, fall back to html2canvas.
 */
async function captureScreenshot(statusEl?: HTMLElement): Promise<string> {
  if (statusEl) {
    statusEl.textContent = t('bugreport.capturing');
    statusEl.style.color = '#fca311';
  }

  // Try Screen Capture API first
  const screenCapture = await captureViaScreenCapture();
  if (screenCapture) return screenCapture;

  // Fallback: html2canvas
  if (statusEl) {
    statusEl.textContent = t('bugreport.capturing_fallback');
  }
  return captureViaHtml2Canvas();
}

// ── Error Collector ────────────────────────────────────────────────────────

function collectErrors(): string[] {
  const errors: string[] = [];

  for (const n of state.notifications ?? []) {
    if (n.level === 'crit' || n.level === 'warn') {
      errors.push(`[${n.level}] ${n.text}${n.sub ? ' — ' + n.sub : ''}`);
    }
  }

  const container = document.getElementById('content');
  if (container) {
    container.querySelectorAll('[style*="color:#e74c3c"], [style*="color:#e63946"], .error, [class*="error"]').forEach(el => {
      const text = (el as HTMLElement).textContent?.trim();
      if (text && text.length < 200) errors.push(text);
    });
  }

  return [...new Set(errors)];
}

// ── Bug Reporter Modal ─────────────────────────────────────────────────────

export async function openBugReporter(): Promise<void> {
  if (document.getElementById('bug-report-overlay')) return;

  const currentScreen = location.hash || '#?';
  const errorMessages = collectErrors();

  const prefill = errorMessages.length > 0
    ? `${t('bugreport.error_messages')}\n${errorMessages.join('\n')}\n\n`
    : '';

  // Capture screenshot BEFORE showing the overlay so it's not in the image
  let screenshotDataUrl = await captureScreenshot();

  const overlay = document.createElement('div');
  overlay.id = 'bug-report-overlay';
  overlay.className = 'overlay';
  overlay.style.cssText = 'z-index:9500;animation:bugFadeIn 0.15s ease';

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.style.cssText = 'width:min(560px,92vw);max-height:90vh;overflow-y:auto;color:var(--text-mid)';

  modal.innerHTML = `
    <style>
      @keyframes bugFadeIn { from { opacity:0 } to { opacity:1 } }
      .bug-title { font-size:16px;font-weight:700;color:var(--text-hi);margin-bottom:14px;font-family:var(--display) }
      .bug-label { font-size:12px;color:var(--text-lo);text-transform:uppercase;letter-spacing:0.08em;margin-bottom:4px }
      .bug-meta { font-size:12px;color:var(--text-dim);margin-top:8px;line-height:1.6 }
      .bug-status { font-size:11px;margin-top:10px;min-height:16px }
      .bug-screenshot-area { margin-bottom:12px;border-radius:3px;overflow:hidden }
      .bug-log-toggle { font-size:11px;color:var(--text-lo);cursor:pointer;margin-top:8px;user-select:none }
      .bug-log-toggle:hover { color:var(--cyan) }
    </style>
    <div class="bug-title">${t('bugreport.title')}</div>
    <div class="bug-screenshot-area" id="bug-screenshot-area"></div>
    <div style="display:flex;gap:8px;margin-bottom:12px">
      <button class="btn sm violet" id="bug-retake">${t('bugreport.btn_retake')}</button>
    </div>
    <div class="bug-label">${t('bugreport.label_desc')}</div>
    <textarea class="field" id="bug-message" style="width:100%;min-height:100px;resize:vertical;box-sizing:border-box" placeholder="${t('bugreport.placeholder')}">${prefill}</textarea>
    <div class="bug-log-toggle" id="bug-log-toggle">${t('bugreport.show_logs')} ▸</div>
    <div id="bug-log-area" style="display:none;margin-top:6px">
      <textarea class="field" id="bug-log-text" style="width:100%;min-height:120px;font-size:10px;resize:vertical;box-sizing:border-box" readonly></textarea>
    </div>
    <div class="bug-meta">
      ${formatVersion(fixture.engineVersion || '?')} · Screen: ${currentScreen} · ${new Date().toLocaleString('de-DE')}
    </div>
    <div class="bug-status" id="bug-status"></div>
    <div style="display:flex;gap:10px;justify-content:flex-end;margin-top:14px">
      <button class="btn sm" id="bug-cancel">${t('bugreport.btn_cancel')}</button>
      <button class="btn sm cyan" id="bug-submit">${t('bugreport.btn_submit')}</button>
    </div>
  `;

  overlay.appendChild(modal);

  // Close on backdrop click
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });

  modal.querySelector('#bug-cancel')!.addEventListener('click', () => overlay.remove());

  // Screenshot area helper
  const screenshotArea = modal.querySelector('#bug-screenshot-area')!;
  function showScreenshot(dataUrl: string): void {
    if (dataUrl) {
      screenshotArea.innerHTML = `<img src="${dataUrl}" style="width:100%;border-radius:3px;border:1px solid rgba(var(--ac),0.15);opacity:0.85;max-height:240px;object-fit:contain;background:#000" />`;
    } else {
      screenshotArea.innerHTML = `<div style="color:#e74c3c;font-size:11px;padding:16px;text-align:center;border:1px dashed rgba(230,57,70,0.3);border-radius:3px">${t('bugreport.screenshot_failed')}</div>`;
    }
  }

  const statusEl = modal.querySelector('#bug-status') as HTMLElement;

  // Show the pre-captured screenshot immediately
  showScreenshot(screenshotDataUrl);

  // Retake button — hide overlay so it's not in the screenshot
  modal.querySelector('#bug-retake')!.addEventListener('click', async () => {
    overlay.style.display = 'none';
    screenshotDataUrl = await captureScreenshot();
    overlay.style.display = '';
    showScreenshot(screenshotDataUrl);
  });

  // Console log toggle
  const logToggle = modal.querySelector('#bug-log-toggle') as HTMLElement;
  const logArea = modal.querySelector('#bug-log-area') as HTMLElement;
  const logText = modal.querySelector('#bug-log-text') as HTMLTextAreaElement;
  logToggle.addEventListener('click', () => {
    const open = logArea.style.display === 'none';
    logArea.style.display = open ? 'block' : 'none';
    logToggle.textContent = (open ? t('bugreport.hide_logs') : t('bugreport.show_logs'))
      + (open ? ' ▾' : ' ▸');
    if (open) logText.value = getRecentLogs();
  });

  // Submit
  modal.querySelector('#bug-submit')!.addEventListener('click', async () => {
    const messageEl = modal.querySelector('#bug-message') as HTMLTextAreaElement;
    const submitBtn = modal.querySelector('#bug-submit') as HTMLButtonElement;
    const message = messageEl.value.trim();

    if (!message) {
      statusEl.textContent = t('bugreport.error_empty');
      statusEl.style.color = '#e74c3c';
      return;
    }

    submitBtn.disabled = true;
    statusEl.textContent = t('bugreport.sending');
    statusEl.style.color = '#fca311';

    try {
      const consoleLogs = getRecentLogs(80);
      const result = await fetchAPI<BugReportResult>('/api/bug-report', {
        method: 'POST',
        body: {
          message,
          screenshot: screenshotDataUrl || '',
          consoleLogs,
          meta: {
            clientVersion: __APP_VERSION__,
            serverVersion: fixture.engineVersion || '?',
            screen: currentScreen,
            userAgent: navigator.userAgent,
            viewport: `${window.innerWidth}x${window.innerHeight}`,
            timestamp: new Date().toISOString(),
          },
        },
      });

      statusEl.innerHTML = `<span style="color:#2ecc71">Bug <a href="${result.issueUrl}" target="_blank" style="color:#00f5d4">#${result.issueNumber}</a> ${t('bugreport.created')}</span>`;
      setTimeout(() => overlay.remove(), 2500);
    } catch (err) {
      statusEl.textContent = `${t('bugreport.error_send')}: ${err instanceof Error ? err.message : 'Unbekannt'}`;
      statusEl.style.color = '#e74c3c';
      submitBtn.disabled = false;
    }
  });

  document.body.appendChild(overlay);

  const textarea = modal.querySelector('#bug-message') as HTMLTextAreaElement;
  textarea?.focus();
  if (prefill) {
    textarea.selectionStart = textarea.selectionEnd = prefill.length;
  }
}
